<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { useRoute, useRouter, createError, useNuxtApp } from '#app';
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useAiTools } from '@/composables/useAiTools';
import { useProjectCreation } from '@/components/use/project';
import { useAuth } from '@/composables/useAuth';
import { useAuthModal } from '@/composables/useAuthModal';
import { Textarea } from '@seede/ui';
import { getLabelsByTag } from '@/lib/prompt-examples';
import { isNoindexTestHost } from '@/lib/site-host';
import AppFooter from '@/components/AppFooter.vue';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildDetailLanguagePaths,
  buildDetailHreflangLinks,
  hreflangFor,
  localizedPath,
  normalizeTranslationAlternates,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

// Import choose reasons images - using hardcoded array (no longer from Sanity)
const chooseReasonsImages = [
  '/ai-design-tools/generation entry.webp',
  '/ai-design-tools/edit.png',
  '/ai-design-tools/no templates.webp',
  '/ai-design-tools/web based.webp',
];

// Generate fine-grained noise texture - Single layer with fine particles and uniform distribution
const generateNoiseTexture = () => {
  if (typeof window === 'undefined') return '';

  // Use larger canvas for finer grain (more pixels = smaller visible grain)
  const canvas = document.createElement('canvas');
  canvas.width = 1024; // Larger canvas = finer grain when scaled
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');

  if (!ctx) return '';

  const imageData = ctx.createImageData(canvas.width, canvas.height);
  const data = imageData.data;

  // Single layer: Generate fine-grained noise pixel by pixel
  // Pure random distribution to avoid any patterns or alternation
  // Noise color: D3A390 (RGB: 211, 163, 144)
  const noiseColorR = 211;
  const noiseColorG = 163;
  const noiseColorB = 144;

  for (let i = 0; i < data.length; i += 4) {
    // Pure random - no position-based patterns
    const random = Math.random();

    // Fine-grained noise: D3A390 color or transparent
    // 50% threshold for balanced distribution
    if (random > 0.5) {
      data[i] = noiseColorR; // R
      data[i + 1] = noiseColorG; // G
      data[i + 2] = noiseColorB; // B
      data[i + 3] = 255; // A
    } else {
      // Transparent for the other half
      data[i] = 0; // R
      data[i + 1] = 0; // G
      data[i + 2] = 0; // B
      data[i + 3] = 0; // A (transparent)
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL();
};

const noiseTextureUrl = ref('');

const route = useRoute();
const router = useRouter();
const slug = computed(() => {
  const raw = route.params.slug;
  return String(Array.isArray(raw) ? raw[0] || '' : raw || '');
});
const pageLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const pageFamily = computed(() => 'ai-design-tools' as const);
const sectionPath = computed(() => `/${pageFamily.value}`);
const collectionBreadcrumbLabel = computed(() => t('landingPages.aiToolsIndex.hero.title'));
const requestURL = useRequestURL();
const canonicalUrl = computed(() =>
  absoluteLocalizedUrl(pageLocale.value, `${sectionPath.value}/${encodeURIComponent(slug.value)}`)
);
const defaultOgImage = `${requestURL.origin}/Veeso_og.jpg`;
const robotsContent = computed(() => {
  const host = requestURL.hostname;
  return isNoindexTestHost(host)
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
});

const { fetchToolBySlug, fetchRelatedTools } = useAiTools();
const expandedFaqItems = ref<Set<number>>(new Set());

// Input box logic (similar to HeroPromptInput but without PromptBubble)
const { createProject, isSubmitting } = useProjectCreation();
const { isAuthenticated } = useAuth();
const { openAuthModal } = useAuthModal();
const { $i18n: i18n } = useNuxtApp() as any;
const locale = computed(() => String(i18n?.locale || 'en'));

const prompt = ref('');
const showSweep = ref(false);
let sweepInterval: ReturnType<typeof setInterval> | null = null;

const textareaRef = ref(null);
// Filter out tool-related words (generator, tool, creator, etc.)
const filterToolWords = (text: string): string => {
  if (!text) return '';

  // Common tool-related words to filter out (case-insensitive)
  const toolWords = [
    'generator',
    'tool',
    'creator',
    'maker',
    'builder',
    'designer',
    'ai generator',
    'free generator',
    'online generator',
    'generator tool',
  ];

  let filtered = text.trim();

  // Remove tool words from the text
  toolWords.forEach((word) => {
    // Remove word at the end (with or without space before it)
    filtered = filtered.replace(new RegExp(`\\s*${word}\\s*$`, 'gi'), '');
    // Remove word at the beginning (with or without space after it)
    filtered = filtered.replace(new RegExp(`^\\s*${word}\\s*`, 'gi'), '');
    // Remove standalone word (with spaces around it)
    filtered = filtered.replace(new RegExp(`\\s+${word}\\s+`, 'gi'), ' ');
  });

  return filtered.trim();
};

// Extract scene keyword from Sanity data for placeholder
// For input placeholder, use heroProductType as primary source
const extractSceneKeyword = (toolData: ToolData | null): string => {
  if (!toolData) return '';

  let keyword = '';

  // Priority: heroProductType (for input placeholder) > sceneKeywords > category
  // heroProductType is the primary field for input placeholder
  if (toolData.heroProductType) {
    keyword = toolData.heroProductType;
  } else if (toolData.sceneKeywords) {
    // Fallback to sceneKeywords if heroProductType is not set
    keyword = toolData.sceneKeywords;
  } else if (toolData.category) {
    // Fallback to category
    keyword = toolData.category;
  }

  // Filter out tool-related words
  return filterToolWords(keyword);
};

// Get scene keyword for H1 display
const sceneKeyword = computed(() => {
  return extractSceneKeyword(tool.value);
});

// Set scene tag and custom placeholder based on tool data
const scene = computed(() => {
  if (!tool.value) return { tag: 'auto', customPlaceholder: null };

  // Extract scene keyword for placeholder
  const keyword = extractSceneKeyword(tool.value);

  // Map to scene tag for functionality
  const titleLine1 = (tool.value.heroTitle || keyword || '').toLowerCase();
  const keywordLower = keyword.toLowerCase();
  let tag = 'auto';

  if (
    keywordLower.includes('poster') ||
    keywordLower.includes('art') ||
    titleLine1.includes('poster')
  ) {
    tag = 'poster';
  } else if (
    keywordLower.includes('social') ||
    keywordLower.includes('instagram') ||
    keywordLower.includes('facebook') ||
    keywordLower.includes('twitter') ||
    titleLine1.includes('social') ||
    titleLine1.includes('instagram')
  ) {
    tag = 'social-media';
  } else if (
    keywordLower.includes('presentation') ||
    keywordLower.includes('slide') ||
    titleLine1.includes('presentation')
  ) {
    tag = 'presentation';
  } else if (keywordLower.includes('carousel') || titleLine1.includes('carousel')) {
    tag = 'carousel';
  }

  // Always set customPlaceholder with keyword
  // This ensures placeholder always shows the scene keyword from Sanity
  const placeholder = keyword || '';
  return { tag, customPlaceholder: placeholder };
});

const getLocale = () => {
  if (i18n.locale === 'zh' || i18n.isChinese) return 'zh';
  if (i18n.locale === 'ja' || i18n.isJapanese) return 'ja';
  return 'en';
};

const placeholderLabels = computed(() => {
  if (scene.value?.customPlaceholder) {
    return [scene.value.customPlaceholder];
  }
  const tag = scene.value?.tag;
  if (!tag) return [];
  return getLabelsByTag(tag, getLocale());
});

const chatState = ref({
  assets: [],
  documents: [],
});

const isDisabled = computed(() => isSubmitting.value || !prompt.value.trim());

// Trigger gradient sweep effect
const triggerSweep = () => {
  showSweep.value = false;
  setTimeout(() => {
    showSweep.value = true;
  }, 50);
};

onMounted(() => {
  setTimeout(() => {
    triggerSweep();
  }, 300);
  sweepInterval = setInterval(() => {
    triggerSweep();
  }, 5000);
});

onUnmounted(() => {
  if (sweepInterval) {
    clearInterval(sweepInterval);
  }
});

const handleSubmit = async () => {
  if (!prompt.value.trim() || isSubmitting.value) return;

  if (!isAuthenticated.value) {
    openAuthModal('sign-up', () => {
      // 登录/注册成功后自动再次触发提交
      handleSubmit();
    });
    return;
  }

  try {
    const project = await createProject({
      prompt: prompt.value,
    });

    handleProjectCreated(project.id);
  } catch (error) {
    console.error('Failed to create project:', error);
  }
};

const handleProjectCreated = (projectId: string) => {
  const projectUrl = new URL(location.href);
  projectUrl.pathname = `/design/${projectId}`;

  navigateTo(projectUrl.href, {
    external: true,
  });
};

interface ToolUseCase {
  title: string;
  description: string;
}

interface ToolStep {
  title: string;
  description: string;
}

interface ToolFaq {
  question: string;
  answer: string;
}

interface ToolTestimonial {
  content: string;
  author: string;
  role: string;
  avatar?: string;
  avatarAlt?: string;
}

interface IntroItem {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
}

interface ToolData {
  subtitle: string;
  category: string;
  sceneKeywords?: string;
  heroImage?: string;
  heroImageAlt?: string;
  heroTitle?: string;

  // Hero Key Benefits
  tagline?: {
    tagline: string;
    data: string;
  }[];

  // Why choose Veeso
  chooseReasons?: IntroItem[];

  // Three Ways to Generate
  generationMethods?: {
    title: string;
    description: string;
    features: string[];
    cta: string;
  }[];

  // Use Cases
  useCases: ToolUseCase[];

  // How to Generate
  howToSteps?: ToolStep[];

  // FAQ
  faq?: ToolFaq[];

  // Testimonials
  testimonials?: ToolTestimonial[];

  // SEO Metadata
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  ogImage?: string;
  ogImageAlt?: string;

  // 页面区块标题
  chooseReasonsSectionCta?: string;
  useCasesSectionTitle?: string;
  testimonialsSectionTitle?: string;
  testimonialsAudienceType?: string;
  howToSectionTitle?: string;
  faqSectionTitle?: string;
  faqSectionDescription?: string;
  finalCtaTitle?: string;
  generationMethodsProductType?: string;
  heroProductType?: string;
  chooseReasonsProductType?: string;
  testimonialsProductType?: string;
  howToProductType?: string;
}

const {
  data: toolData,
  pending,
  error: toolError,
} = await useAsyncData(
  () => `ai-tool:${pageFamily.value}:${pageLocale.value}:${slug.value}`,
  async () => {
    try {
      const data = await fetchToolBySlug(slug.value, pageLocale.value);
      if (!data) {
        throw createError({ statusCode: 404, statusMessage: 'Tool not found' });
      }
      return data;
    } catch (error: any) {
      if (error.statusCode === 404) {
        throw error;
      }
      console.error('Error fetching tool:', error);
      return null;
    }
  },
  {
    server: true,
    lazy: false,
    default: () => null,
    watch: [slug, pageLocale, pageFamily],
  }
);

// SSR must preserve a real 404 for missing localized Tools documents.
if (toolError.value) {
  throw toolError.value;
}

// Fetch related tools automatically (excluding current tool)
interface RelatedTool {
  _id: string;
  slug: string;
  sceneKeywords?: string;
  heroTitle?: string;
  image?: string;
  heroImageAlt?: string;
}

const { data: relatedToolsDataRaw } = await useAsyncData<RelatedTool[]>(
  () => `related-tools:${pageFamily.value}:${pageLocale.value}:${slug.value}`,
  async () => {
    try {
      const tools = await fetchRelatedTools(slug.value, 4, pageLocale.value);
      return (tools || []) as RelatedTool[];
    } catch (error) {
      console.error('Error fetching related tools:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
    watch: [slug, pageLocale, pageFamily],
  }
);

const relatedToolsData = computed(() => {
  if (!relatedToolsDataRaw.value || !Array.isArray(relatedToolsDataRaw.value)) {
    return [];
  }
  return relatedToolsDataRaw.value;
});

// Transform Sanity data to match component interface
const tool = computed(() => {
  if (!toolData.value) return null;

  const data = toolData.value;
  return {
    subtitle: data.subtitle || '',
    category: data.category || '',
    sceneKeywords: data.sceneKeywords || null,
    heroImage: data.heroImage || null,
    heroImageAlt: data.heroImageAlt || null,
    heroProductType:
      data.heroProductType || t('landingPages.aiToolsDetail.hero.product_type_fallback'),
    heroTitle: data.heroTitle || null,
    tagline: data.tagline || [],
    chooseReasons: (data.chooseReasons || []).map((item: any) => ({
      title: item.title || '',
      description: item.description || '',
      image: typeof item.image === 'string' && item.image.trim() ? item.image : undefined,
      imageAlt:
        typeof item.imageAlt === 'string' && item.imageAlt.trim() ? item.imageAlt : undefined,
    })),
    generationMethods: (data.generationMethods || []).map((method: any) => ({
      title: method.title || '',
      description: method.description || '',
      features: method.features || [],
      cta: method.cta || '',
    })),
    useCases: (data.useCases || []).map((uc: any) => ({
      title: uc.title || '',
      description: uc.description || '',
    })),
    howToSteps: (data.howToSteps || []).map((step: any) => ({
      title: step.title || '',
      description: step.description || '',
    })),
    faq: (data.faq || []).map((item: any) => ({
      question: item.question || '',
      answer: item.answer || '',
    })),
    testimonials: (data.testimonials || []).map((t: any) => ({
      content: t.content || '',
      author: t.author || '',
      role: t.role || '',
      avatar: t.avatar || null,
      avatarAlt: t.avatarAlt || null,
    })),
    seoTitle: data.seoTitle || '',
    seoDescription: data.seoDescription || '',
    keywords: data.keywords || [],
    ogImage: data.ogImage,
    ogImageAlt: data.ogImageAlt,
    // 页面区块标题
    generationMethodsProductType:
      data.generationMethodsProductType ||
      t('landingPages.aiToolsDetail.generation_methods.section_product_fallback'),
    chooseReasonsProductType:
      (data.chooseReasonsProductType && data.chooseReasonsProductType.trim()) ||
      data.heroProductType ||
      data.sceneKeywords ||
      t('landingPages.aiToolsDetail.generation_methods.section_product_fallback'),
    chooseReasonsSectionCta:
      data.chooseReasonsSectionCta || t('landingPages.aiToolsDetail.choose_reasons.cta'),
    useCasesSectionTitle:
      data.useCasesSectionTitle || t('landingPages.aiToolsDetail.use_cases.title_fallback'),
    testimonialsSectionTitle: (
      data.testimonialsSectionTitle || t('landingPages.aiToolsDetail.testimonials.title_fallback')
    ).replace(
      '{{audienceType}}',
      data.testimonialsAudienceType ||
        t('landingPages.aiToolsDetail.testimonials.audience_fallback')
    ),
    testimonialsAudienceType:
      data.testimonialsAudienceType ||
      t('landingPages.aiToolsDetail.testimonials.audience_fallback'),
    howToSectionTitle: (
      data.howToSectionTitle || t('landingPages.aiToolsDetail.how_to.title_fallback')
    ).replace(
      '{{productType}}',
      data.howToProductType || t('landingPages.aiToolsDetail.how_to.product_type_fallback')
    ),
    howToProductType:
      data.howToProductType || t('landingPages.aiToolsDetail.how_to.product_type_fallback'),
    faqSectionTitle: data.faqSectionTitle || t('landingPages.aiToolsDetail.faq.title'),
    faqSectionDescription: data.faqSectionDescription || '',
    finalCtaTitle: (
      data.finalCtaTitle || `Ready to Boost Your ${data.category || ''} Presence?`
    ).replace('{{category}}', data.category || ''),
  } as ToolData;
});

// Redirect if tool doesn't exist (only after data has finished loading)
watch([() => toolData.value, () => pending.value], ([data, isLoading]) => {
  // Only redirect if data loading is complete and tool is not found
  if (!isLoading && data === null && typeof window !== 'undefined') {
    router.push(localizedPath(pageLocale.value, sectionPath.value));
  }
});

// Handle 404 errors
watch(
  () => toolError.value,
  (error) => {
    if (error && error.statusCode === 404 && typeof window !== 'undefined') {
      router.push(localizedPath(pageLocale.value, sectionPath.value));
    }
  }
);

function goToWorkspace() {
  router.push('/workspace');
}

function _goBack() {
  router.push(localizedPath(pageLocale.value, sectionPath.value));
}

function toggleFaqItem(index: number) {
  if (expandedFaqItems.value.has(index)) {
    expandedFaqItems.value.clear();
  } else {
    expandedFaqItems.value.clear();
    expandedFaqItems.value.add(index);
  }
}

function isFaqExpanded(index: number) {
  return expandedFaqItems.value.has(index);
}

// function formatSceneName(scene: string): string {
//   if (scene === 'all') return 'All';
//   const sceneNames: Record<string, string> = {
//     fashion: 'Fashion',
//     electronics: 'Electronics',
//     toys: 'Toys',
//     'home-kitchen': 'Home & Kitchen',
//     beauty: 'Beauty & Personal Care',
//     sports: 'Sports & Outdoors',
//     books: 'Books & Media',
//     automotive: 'Automotive',
//   };
//   return (
//     sceneNames[scene] ||
//     scene
//       .split('-')
//       .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//       .join(' ')
//   );
// }

function handleEscapeKey(_event: KeyboardEvent) {
  // Handle escape key for other modals if needed
}

// Avatar files in public/avatar folder
const avatarFiles = [
  'bird.png',
  'cat.png',
  'chihuahua.png',
  'dog.png',
  'face-of--maine-coon.png',
  'face-of--ragdoll.png',
  'face-of-british-shorthair.png',
  'pomeranian.png',
  'shih-tzu.png',
  'spix-s-macaw.png',
  'toy-poodle.png',
];

// Get random avatar for each testimonial
const getRandomAvatar = (index: number) => {
  // Use index to ensure consistent avatar per testimonial
  const avatarIndex = index % avatarFiles.length;
  return `/avatar/${avatarFiles[avatarIndex]}`;
};

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKey);
  // Generate noise texture on client side
  if (typeof window !== 'undefined') {
    noiseTextureUrl.value = generateNoiseTexture();
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey);
});

// Always emit a canonical URL, even if Sanity temporarily fails and tool data is null.
// This prevents Google from seeing route pages as duplicate pages without a user-selected canonical.
const translationAlternates = computed(() =>
  normalizeTranslationAlternates(
    (toolData.value as { alternates?: unknown } | null)?.alternates,
    slug.value ? { language: pageLocale.value, slug: slug.value } : undefined
  )
);

const alternateHeadLinks = computed(() =>
  buildDetailHreflangLinks(pageFamily.value, translationAlternates.value)
);
const detailLanguagePaths = computed(() =>
  buildDetailLanguagePaths(pageFamily.value, translationAlternates.value)
);
provideSeoDetailLanguagePaths(detailLanguagePaths);

useHead(() => ({
  htmlAttrs: { lang: pageLocale.value },
  link: [
    { rel: 'canonical', href: canonicalUrl.value, key: 'canonical' },
    ...alternateHeadLinks.value,
  ],
  meta: [
    { key: 'robots', name: 'robots', content: robotsContent.value },
    { key: 'og:url', property: 'og:url', content: canonicalUrl.value },
    { key: 'og:locale', property: 'og:locale', content: ogLocaleFor(pageLocale.value) },
    { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
  ],
}));

// SEO Meta
if (tool.value) {
  const keywordsStr = Array.isArray(tool.value.keywords)
    ? tool.value.keywords.join(', ')
    : tool.value.keywords || '';

  const ogImage = tool.value.ogImage || tool.value.heroImage || defaultOgImage;
  const displayTitle =
    tool.value.heroTitle ||
    tool.value.sceneKeywords ||
    t('landingPages.aiToolsDetail.fallback.title');
  const ogImageAlt = tool.value.ogImageAlt || tool.value.heroImageAlt || displayTitle;

  const metaTags = [
    { name: 'description', content: tool.value.seoDescription },
    { name: 'keywords', content: keywordsStr },
    // Open Graph
    { property: 'og:title', content: tool.value.seoTitle },
    { property: 'og:description', content: tool.value.seoDescription },
    { property: 'og:type', content: 'website' },
    { name: 'language', content: hreflangFor(pageLocale.value) },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(pageLocale.value) },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:alt', content: ogImageAlt },
    // Twitter Card (using Open Graph data as fallback)
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: tool.value.seoTitle },
    { name: 'twitter:description', content: tool.value.seoDescription },
    { name: 'twitter:image', content: ogImage },
    { name: 'twitter:image:alt', content: ogImageAlt },
  ];

  // Schema.org Structured Data
  const structuredData: any[] = [];

  // Base Schema.org structured data (always enabled)
  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'LandingPage',
    name: tool.value.seoTitle,
    description: tool.value.seoDescription,
    url: canonicalUrl.value,
    inLanguage: hreflangFor(pageLocale.value),
    ...(ogImage ? { image: ogImage } : {}),
  };

  structuredData.push(baseSchema);

  // FAQ Schema (always enabled if FAQ exists)
  if (tool.value.faq && tool.value.faq.length > 0) {
    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: tool.value.faq.map((item: any) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  // Breadcrumb Schema (always enabled)
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('landingPages.articleDetail.breadcrumb.home'),
        item: 'https://veeso.ai/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: collectionBreadcrumbLabel.value,
        item: absoluteLocalizedUrl(pageLocale.value, sectionPath.value),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name:
          tool.value.heroTitle ||
          tool.value.sceneKeywords ||
          t('landingPages.aiToolsDetail.fallback.title'),
        item: canonicalUrl.value,
      },
    ],
  });

  useHead({
    title: tool.value.seoTitle,
    meta: metaTags,
    htmlAttrs: { lang: pageLocale.value },
    link: [
      { rel: 'canonical', href: canonicalUrl.value, key: 'canonical' },
      ...alternateHeadLinks.value,
    ],
    script: structuredData.map((schema) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    })),
  });
}
</script>

<template>
  <div
    class="ai-design-tools-page min-h-screen pt-[60px] relative bg-[#fafafa]"
    :data-locale="locale"
  >
    <AppNav :active-path="sectionPath" />

    <!-- Loading state -->
    <div v-if="pending" class="container mx-auto px-4 py-24 text-center">
      <p class="text-lg text-black/60">{{ t('landingPages.aiToolsDetail.loading') }}</p>
    </div>

    <!-- Error state -->
    <div v-else-if="toolError" class="container mx-auto px-4 py-24 text-center">
      <p class="text-lg text-red-600">{{ t('ui.tools.errorRedirecting') }}</p>
    </div>

    <!-- Tool content -->
    <div v-else-if="tool">
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-[1280px] mx-auto pt-[64.5px] pb-6">
          <nav class="flex items-center gap-2 h-5">
            <a
              href="/"
              class="hover:opacity-80 transition-opacity"
              style="
                font-family:
                  'SF Pro',
                  -apple-system,
                  BlinkMacSystemFont,
                  sans-serif;
                font-size: 13.3297px;
                line-height: 20px;
                color: rgba(0, 0, 0, 0.6);
              "
            >
              {{ t('landingPages.articleDetail.breadcrumb.home') }}
            </a>
            <svg class="w-4 h-4" style="margin-top: 2px" fill="none" viewBox="0 0 24 24">
              <path
                d="M9 5l7 7-7 7"
                stroke="rgba(0, 0, 0, 0.3)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <a
              :href="localizedPath(pageLocale, sectionPath)"
              class="hover:opacity-80 transition-opacity"
              style="
                font-family:
                  'SF Pro',
                  -apple-system,
                  BlinkMacSystemFont,
                  sans-serif;
                font-size: 13.3557px;
                line-height: 20px;
                color: rgba(0, 0, 0, 0.6);
              "
            >
              {{ collectionBreadcrumbLabel }}
            </a>
            <svg class="w-4 h-4" style="margin-top: 2px" fill="none" viewBox="0 0 24 24">
              <path
                d="M9 5l7 7-7 7"
                stroke="rgba(0, 0, 0, 0.3)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span
              style="
                font-family:
                  'SF Pro',
                  -apple-system,
                  BlinkMacSystemFont,
                  sans-serif;
                font-weight: 510;
                font-size: 12.9206px;
                line-height: 20px;
                color: rgba(0, 0, 0, 0.9);
              "
            >
              {{
                tool.heroTitle ||
                tool.sceneKeywords ||
                t('landingPages.aiToolsDetail.fallback.title')
              }}
            </span>
          </nav>
        </div>

        <section class="max-w-[1280px] mx-auto py-12 mb-24">
          <div class="flex items-stretch flex-col lg:flex-row gap-0 lg:gap-[57px]">
            <div class="w-full lg:max-w-[598px] lg:mr-0 mb-12 lg:mb-0 flex flex-col">
              <h1
                class="font-boldonse text-[37px] leading-[62px] font-normal mb-0 tracking-[-0.8px]"
                style="color: #1a1a1a"
              >
                {{
                  tool.heroTitle || sceneKeyword || t('landingPages.aiToolsDetail.fallback.title')
                }}
              </h1>
              <p
                class="text-[20px] leading-[29px] text-black/50 mb-12 font-normal tracking-[-0.22px] mt-6"
              >
                {{ tool.subtitle }}
              </p>

              <div class="mb-6">
                <div class="w-full max-w-[720px] input-wrapper">
                  <div
                    class="prompt-input-container rounded-3xl bg-white/80 backdrop-blur-[10px] border-[0.5px] border-black/15 overflow-hidden shadow-[0_0_20px_0_rgba(0,0,0,0.03)]"
                    :class="{ 'sweep-active': showSweep }"
                  >
                    <Textarea
                      ref="textareaRef"
                      v-model="prompt"
                      :loading="isSubmitting"
                      :placeholderLabels="placeholderLabels"
                      :chatState="chatState"
                      class="bg-transparent"
                      @keydown.enter.meta="handleSubmit"
                      @keydown.enter.ctrl="handleSubmit"
                    />
                    <div class="px-2 pb-2 flex items-center justify-end">
                      <button
                        type="button"
                        :disabled="isDisabled"
                        class="px-4 py-2 rounded-full bg-[#ff6b35] text-white font-medium text-sm hover:bg-[#ff7d4d] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        @click="handleSubmit"
                      >
                        <span v-if="isSubmitting">{{
                          t('landingPages.aiToolsDetail.hero.designing')
                        }}</span>
                        <span v-else>{{ t('landingPages.aiToolsDetail.hero.design') }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="tool.tagline" class="grid grid-cols-3 gap-[16px] mt-auto">
                <div v-for="(stat, idx) in tool.tagline" :key="idx" class="text-center">
                  <div class="text-lg leading-7 font-normal text-black/90 mb-0">
                    {{ stat.data }}
                  </div>
                  <div class="text-xs leading-[15px] text-black/50 mt-[2px]">
                    {{ stat.tagline }}
                  </div>
                </div>
              </div>
            </div>

            <div class="w-full lg:w-[593px] flex flex-col">
              <div
                class="checkerboard-background w-full h-full rounded-[8px] overflow-hidden flex items-center justify-center relative"
                style="aspect-ratio: 1/1; max-height: 593px"
              >
                <img
                  v-if="tool?.heroImage || tool?.ogImage"
                  :src="tool.heroImage || tool.ogImage"
                  :alt="
                    tool.heroImageAlt ||
                    tool.ogImageAlt ||
                    tool?.heroTitle ||
                    tool?.sceneKeywords ||
                    'Featured image'
                  "
                  class="w-full h-full object-cover block m-0 p-0"
                />
              </div>
            </div>
          </div>
        </section>

        <section v-if="tool.generationMethods" class="max-w-[1280px] mx-auto mb-24 pt-16">
          <div class="text-center mb-12">
            <h2
              class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 mb-4 tracking-[-0.8px]"
            >
              {{ t('landingPages.aiToolsDetail.generation_methods.title_prefix') }}
              {{
                tool.generationMethodsProductType ||
                t('landingPages.aiToolsDetail.generation_methods.product_type_fallback')
              }}
            </h2>
          </div>
          <div class="grid md:grid-cols-3 gap-[24px]">
            <div
              v-for="(method, index) in tool.generationMethods"
              :key="index"
              class="bg-white rounded-[24px] p-8 border border-black/5 hover:border-black/10 transition-all duration-300 flex flex-col"
              :class="
                index === 0
                  ? 'bg-gradient-to-b from-[#FCF9F3] to-white'
                  : index === 1
                    ? 'bg-gradient-to-b from-[#F0F5FB] to-white'
                    : 'bg-gradient-to-b from-[#F4F0F6] to-white'
              "
            >
              <h3 class="text-xl leading-7 font-bold text-black/90 mb-2">
                {{ method.title }}
              </h3>
              <p class="text-sm leading-[22px] text-black/60 mb-6" style="font-size: 14px">
                {{ method.description }}
              </p>

              <ul class="space-y-[12px] mb-6 flex-1">
                <li
                  v-for="(feature, idx) in method.features"
                  :key="idx"
                  class="flex items-start text-sm leading-5 text-black/70"
                >
                  <svg
                    class="w-4 h-4 text-[#16A34A] mr-2 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span style="font-size: 14px">{{ feature }}</span>
                </li>
              </ul>

              <a
                href="/workspace"
                @click.prevent="goToWorkspace"
                class="w-full h-12 px-6 py-2 rounded-full font-bold text-sm leading-5 text-white/90 bg-black/90 hover:scale-105 transition-all duration-300 mt-auto flex items-center justify-center no-underline cursor-pointer"
              >
                {{ $t('ui.visibleCopy.pagesAiDesignToolsSlug.text1') }}
              </a>
            </div>
          </div>
        </section>

        <section
          v-if="tool.chooseReasons"
          class="max-w-[1280px] mx-auto mb-24 pt-16 feature-section"
        >
          <h2
            class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 mb-16 text-center tracking-[-0.8px]"
          >
            {{ $t('ui.visibleCopy.pagesAiDesignToolsSlug.text2') }}
            {{ tool.chooseReasonsProductType }}
          </h2>
          <div class="space-y-[128px] feature-grid-container">
            <div
              v-for="(item, index) in tool.chooseReasons"
              :key="index"
              class="grid gap-12"
              :class="index % 2 === 0 ? 'lg:grid-cols-[1fr_2fr]' : 'lg:grid-cols-[2fr_1fr]'"
            >
              <div
                class="flex flex-col justify-center"
                :class="index % 2 === 1 ? 'lg:order-2' : ''"
              >
                <h3
                  class="text-[28px] leading-[31px] font-normal text-black/90 mb-4"
                  style="font-weight: 274"
                >
                  {{ item.title }}
                </h3>
                <p
                  class="text-base leading-[26px] text-black/60 tracking-[-0.16px] mb-6"
                  style="font-size: 15.52px"
                >
                  {{ item.description }}
                </p>
                <a
                  href="/workspace"
                  @click.prevent="goToWorkspace"
                  class="w-fit h-9 px-6 py-2 rounded-full font-normal text-sm leading-4 text-white/90 bg-black/90 hover:scale-105 transition-all duration-300 inline-flex items-center justify-center no-underline cursor-pointer"
                >
                  {{ tool.chooseReasonsSectionCta }}
                </a>
              </div>
              <div :class="index % 2 === 1 ? 'lg:order-1' : ''">
                <img
                  :src="item.image || chooseReasonsImages[index] || chooseReasonsImages[0]"
                  :alt="item.imageAlt || item.title || 'Feature image'"
                  class="w-full h-auto block"
                  style="
                    border: 1px solid rgba(0, 0, 0, 0.05);
                    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05);
                    border-radius: 18px;
                    display: block;
                    margin: 0;
                    padding: 0;
                  "
                />
              </div>
            </div>
          </div>
        </section>

        <section class="max-w-[1460px] mx-auto mb-24 pt-12">
          <div
            class="bg-gradient-to-b from-white to-[#F0F5FB] rounded-[18px] border border-black/5 shadow-[0px_4px_16px_rgba(0,0,0,0.05)]"
            style="padding: 64px 0"
          >
            <h2
              class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 text-center tracking-[-0.8px]"
              style="color: rgba(0, 0, 0, 0.9); margin-bottom: 64px"
            >
              {{ tool.useCasesSectionTitle }}
            </h2>
            <div
              class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              style="max-width: 1152px; margin-left: auto; margin-right: auto; padding: 0 24px"
            >
              <div
                v-for="(useCase, index) in tool.useCases"
                :key="index"
                class="bg-white rounded-[24px] border border-black/5"
                style="
                  box-sizing: border-box;
                  padding: 24px;
                  min-height: 292px;
                  display: flex;
                  flex-direction: column;
                "
              >
                <div style="flex: 1">
                  <h3
                    class="text-[28px] leading-[31px] text-black/90 mb-4"
                    style="
                      font-family:
                        'SF Pro',
                        -apple-system,
                        BlinkMacSystemFont,
                        sans-serif;
                      font-weight: 274;
                      color: rgba(0, 0, 0, 0.9);
                      margin-bottom: 16px;
                    "
                  >
                    {{ useCase.title }}
                  </h3>
                  <p
                    class="text-black/60 leading-[26px]"
                    style="
                      font-family:
                        'SF Pro',
                        -apple-system,
                        BlinkMacSystemFont,
                        sans-serif;
                      font-weight: 400;
                      font-size: 15.5px;
                      line-height: 26px;
                      color: rgba(0, 0, 0, 0.6);
                      margin-bottom: 0;
                    "
                  >
                    {{ useCase.description }}
                  </p>
                </div>
                <div
                  class="mt-auto"
                  style="
                    width: 32px;
                    height: 4px;
                    background: rgba(0, 0, 0, 0.05);
                    border-radius: 9999px;
                    margin-top: auto;
                  "
                ></div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="tool.testimonials" class="max-w-[1280px] mx-auto mb-24 pt-12">
          <h2
            class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 mb-12 text-center tracking-[-0.8px]"
          >
            {{ tool.testimonialsSectionTitle }}
          </h2>
          <div class="grid md:grid-cols-3 gap-[32px]">
            <div
              v-for="(testimonial, index) in tool.testimonials"
              :key="index"
              class="bg-white rounded-[24px] p-8 border border-black/5 flex flex-col h-full"
            >
              <div class="flex items-center gap-1 mb-6">
                <svg
                  v-for="i in 5"
                  :key="i"
                  class="w-6 h-6 text-[#FACC15]"
                  viewBox="0 0 24 22"
                  fill="currentColor"
                >
                  <path
                    d="M12.3235 0 L15.5781 7.53423 L23.7462 8.2918 L17.5833 13.7058 L19.3869 21.7082 L12.3235 17.52 L12.3235 0Z"
                  />
                  <path
                    d="M12.3335 0 L12.3335 17.52 L5.28007 21.7082 L7.08366 13.7058 L0.920818 8.2918 L9.08892 7.53423 L12.3335 0Z"
                  />
                </svg>
              </div>
              <p class="text-black/70 leading-[26px] mb-6 flex-1" style="font-size: 16px">
                {{ testimonial.content }}
              </p>
              <div class="flex items-center mt-auto">
                <div class="w-10 h-10 rounded-full overflow-hidden mr-4 flex-shrink-0">
                  <img
                    :src="testimonial.avatar || getRandomAvatar(index)"
                    :alt="testimonial.avatarAlt || testimonial.author || 'Testimonial author'"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div class="font-bold text-base leading-6 text-black/90">
                    {{ testimonial.author }}
                  </div>
                  <div class="text-sm leading-5 text-black/60">{{ testimonial.role }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="tool.howToSteps" class="max-w-[1280px] mx-auto mb-24 pt-12">
          <h2
            class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 mb-12 text-center tracking-[-0.8px]"
          >
            {{ tool.howToSectionTitle }}
          </h2>
          <div
            class="grid gap-[32px] mt-12"
            :class="tool.howToSteps.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'"
          >
            <div
              v-for="(step, idx) in tool.howToSteps"
              :key="idx"
              class="group relative bg-white rounded-[24px] p-8 border border-black/5 hover:border-black/10 transition-all duration-300"
            >
              <div
                class="absolute -top-[11px] -left-[9px] w-[38.4px] h-[38.4px] rounded-full bg-black flex items-center justify-center text-white text-sm font-bold shadow-[0px_3px_6px_rgba(0,0,0,0.07)]"
              >
                {{ idx + 1 }}
              </div>
              <h3
                class="text-[28px] leading-[31px] text-black/90 mb-3 mt-4"
                style="
                  font-family:
                    'SF Pro',
                    -apple-system,
                    BlinkMacSystemFont,
                    sans-serif;
                  font-weight: 274;
                  color: rgba(0, 0, 0, 0.9);
                "
              >
                {{ step.title }}
              </h3>
              <p class="text-base leading-[26px] text-black/60" style="font-size: 16px">
                {{ step.description }}
              </p>
            </div>
          </div>
        </section>

        <section v-if="tool.faq" class="max-w-[576px] mx-auto mb-24 pt-12">
          <div class="text-center" style="margin-bottom: 48px">
            <h2
              class="font-boldonse text-[28px] leading-[62px] font-normal text-black/90 mb-0 tracking-[-0.8px]"
              style="
                font-style: normal;
                font-weight: 400;
                font-size: 28px;
                line-height: 62px;
                text-align: center;
                color: rgba(0, 0, 0, 0.9);
              "
            >
              {{ tool.faqSectionTitle }}
            </h2>
            <p
              v-if="tool.faqSectionDescription"
              class="text-black/60 mt-0"
              style="
                font-family:
                  'SF Pro',
                  -apple-system,
                  BlinkMacSystemFont,
                  sans-serif;
                font-style: normal;
                font-weight: 400;
                font-size: 18px;
                line-height: 28px;
                text-align: center;
                color: rgba(0, 0, 0, 0.6);
                margin-top: 15.59px;
              "
            >
              {{ tool.faqSectionDescription }}
            </p>
          </div>

          <div class="max-w-[576px] mx-auto">
            <div>
              <div
                v-for="(item, index) in tool.faq"
                :key="index"
                class="transition-all duration-300"
                :style="{
                  boxSizing: 'border-box',
                  width: '576px',
                  minHeight: '77px',
                  borderBottom:
                    index === tool.faq.length - 1 ? 'none' : '1px solid rgba(0, 0, 0, 0.05)',
                }"
              >
                <button
                  type="button"
                  @click="toggleFaqItem(index)"
                  class="flex items-center justify-between w-full text-left transition-all relative"
                  style="position: relative; width: 576px; height: 60px; padding: 0"
                >
                  <h3
                    class="pr-4 m-0"
                    style="
                      font-family:
                        'SF Pro',
                        -apple-system,
                        BlinkMacSystemFont,
                        sans-serif;
                      font-style: normal;
                      font-weight: 510;
                      font-size: 18.34px;
                      line-height: 28px;
                      color: rgba(0, 0, 0, 0.9);
                      position: absolute;
                      left: 0px;
                      top: 16px;
                    "
                  >
                    {{ item.question }}
                  </h3>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    class="shrink-0 transition-transform duration-200"
                    :class="[isFaqExpanded(index) ? 'rotate-90' : '']"
                    style="
                      position: absolute;
                      width: 20px;
                      height: 20px;
                      left: 556px;
                      top: 20px;
                      color: rgba(0, 0, 0, 0.4);
                    "
                  >
                    <path
                      fill="currentColor"
                      d="M9.47 6.47a.75.75 0 0 1 1.06 0l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 1 1-1.06-1.06L13.94 12 9.47 7.53a.75.75 0 0 1 0-1.06"
                    ></path>
                  </svg>
                </button>
                <div
                  v-show="isFaqExpanded(index)"
                  class="transition-all duration-200 ease-in-out"
                  style="padding-top: 17px; padding-bottom: 20px"
                >
                  <p
                    class="text-black/70 font-normal"
                    style="
                      font-family:
                        'SF Pro',
                        -apple-system,
                        BlinkMacSystemFont,
                        sans-serif;
                      font-size: 16px;
                      line-height: 26px;
                      color: rgba(0, 0, 0, 0.7);
                    "
                  >
                    {{ item.answer }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="relative mx-auto text-center overflow-hidden mb-24 mt-24">
          <div
            class="relative w-full rounded-[32px] overflow-hidden"
            style="width: 1504px; max-width: 100%; height: 626px"
          >
            <!-- Background image -->
            <img
              :src="'/ai-design-tools/CTAimage.webp'"
              :alt="t('landingPages.aiToolsDetail.cta.image_alt')"
              class="absolute inset-0 w-full h-full object-cover"
              @error="
                (e: Event) => {
                  const target = e.target as HTMLImageElement | null;
                  if (target) {
                    console.error('CTA image failed to load:', target.src);
                  }
                }
              "
              loading="eager"
            />
            <!-- Content container -->
            <div class="relative z-10 w-full h-full flex flex-col items-center justify-center">
              <!-- Title -->
              <h2
                class="font-boldonse text-white tracking-[-0.8px]"
                style="
                  font-style: normal;
                  font-weight: 400;
                  font-size: 41px;
                  line-height: 54px;
                  text-align: center;
                  letter-spacing: -0.8px;
                  color: #ffffff;
                  width: 517.63px;
                  max-width: 90%;
                  margin-bottom: 64px;
                "
              >
                {{ tool.finalCtaTitle }}
              </h2>
              <!-- Button -->
              <a
                href="/workspace"
                @click.prevent="goToWorkspace"
                class="inline-flex items-center h-[61px] px-8 py-4 rounded-full bg-white hover:scale-105 transition-all duration-300 shadow-xl no-underline cursor-pointer"
                style="width: 254px; border-radius: 9999px; margin-bottom: 32px"
              >
                <span
                  style="
                    font-family:
                      'SF Pro',
                      -apple-system,
                      BlinkMacSystemFont,
                      sans-serif;
                    font-style: normal;
                    font-weight: 700;
                    font-size: 16.8794px;
                    line-height: 28px;
                    color: #000000;
                    display: flex;
                    align-items: center;
                  "
                >
                  {{ $t('ui.visibleCopy.pagesAiDesignToolsSlug.text3') }}
                </span>
                <svg
                  class="ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  style="width: 20px; height: 20px; color: #000000"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
              <!-- Footer text -->
              <p
                class="text-center"
                :style="{
                  width: '412.82px',
                  maxWidth: '90%',
                  height: '20px',
                  fontFamily: 'SF Pro',
                  fontStyle: 'normal',
                  fontWeight: 590,
                  fontSize: '12px',
                  lineHeight: '20px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.8)',
                  marginTop: 0,
                }"
              >
                {{ $t('ui.visibleCopy.pagesAiDesignToolsSlug.text4') }}
              </p>
            </div>
          </div>
        </section>

        <section
          v-if="relatedToolsData && relatedToolsData.length > 0"
          class="max-w-[1280px] mx-auto mb-24 pt-12 related-tools-section"
        >
          <h2
            class="font-boldonse text-[28px] leading-[60px] font-normal text-black/90 mb-0 text-center tracking-[-0.8px]"
          >
            {{ t('landingPages.aiToolsDetail.explore_more') }}
          </h2>
          <p class="text-center text-lg leading-7 text-black/60 mb-8 mt-[17px]">
            {{ $t('ui.visibleCopy.pagesAiDesignToolsSlug.text5') }}
          </p>
          <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6 related-tools-grid">
            <NuxtLink
              v-for="(relatedTool, index) in relatedToolsData"
              :key="relatedTool._id || index"
              :to="localizedPath(pageLocale, `${sectionPath}/${relatedTool.slug}`)"
              class="group cursor-pointer bg-white rounded-[20px] overflow-hidden border border-black/5 hover:border-black/10 transition-all duration-300 hover:shadow-lg block"
            >
              <div class="aspect-video overflow-hidden bg-black/5">
                <img
                  v-if="relatedTool.image"
                  :src="relatedTool.image"
                  :alt="
                    relatedTool.heroImageAlt ||
                    relatedTool.heroTitle ||
                    relatedTool.sceneKeywords ||
                    t('landingPages.aiToolsDetail.fallback.title')
                  "
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div class="p-6 text-center">
                <h3 class="text-lg font-bold text-black/90">
                  {{
                    relatedTool.heroTitle ||
                    relatedTool.sceneKeywords ||
                    t('landingPages.aiToolsDetail.fallback.title')
                  }}
                </h3>
              </div>
            </NuxtLink>
          </div>
        </section>
      </div>
      <AppFooter />
    </div>
    <!-- End of tool content -->
  </div>
</template>

<style scoped>
.checkerboard-background {
  background-color: #f4f4f4;
  background-image:
    linear-gradient(45deg, #e2e2e2 25%, transparent 25%),
    linear-gradient(-45deg, #e2e2e2 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e2e2e2 75%),
    linear-gradient(-45deg, transparent 75%, #e2e2e2 75%);
  background-position:
    0 0,
    0 12px,
    12px -12px,
    -12px 0;
  background-size: 24px 24px;
}

/* Gradient sweep effect for input box */
.prompt-input-container {
  position: relative;
}

.prompt-input-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 107, 53, 0.15) 10%,
    rgba(247, 201, 75, 0.2) 25%,
    rgba(74, 222, 128, 0.2) 40%,
    rgba(34, 211, 238, 0.2) 55%,
    rgba(167, 139, 250, 0.15) 70%,
    rgba(244, 114, 182, 0.1) 85%,
    transparent 100%
  );
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  z-index: 1;
}

.prompt-input-container.sweep-active::before {
  opacity: 1;
  animation: gradient-sweep 0.4s ease-out forwards;
}

@keyframes gradient-sweep {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.prompt-input-container > * {
  position: relative;
  z-index: 2;
}

/* Responsive adjustments */
@media screen and (max-width: 1200px) {
  section.feature-section {
    padding-top: 80px;
    padding-bottom: 80px;
  }

  section.feature-section .feature-grid-container > div {
    gap: 50px;
  }
}

@media screen and (max-width: 992px) {
  section.feature-section .feature-grid-container > div {
    gap: 32px;
  }

  section.feature-section .feature-grid-container > div:not(:last-child) {
    margin-bottom: 70px;
  }

  section.feature-section h3 {
    font-size: 24px;
  }

  /* Results grid: 4 cols -> 2 cols */
  section.related-tools-section .related-tools-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
}

@media screen and (max-width: 768px) {
  section.feature-section {
    padding-top: 56px;
    padding-bottom: 56px;
  }

  section.feature-section h2 {
    font-size: 32px;
    margin-bottom: 40px;
  }

  section.feature-section .feature-grid-container > div {
    grid-template-columns: 1fr !important;
    max-width: 500px;
    margin: 0 auto;
  }

  section.feature-section .feature-grid-container > div:not(:last-child) {
    margin-bottom: 80px;
  }

  section.related-tools-section {
    padding-top: 56px;
    padding-bottom: 56px;
  }

  section.related-tools-section h2 {
    font-size: 32px;
    margin-bottom: 40px;
  }

  /* Results grid: 2 cols -> 1 col */
  section.related-tools-section .related-tools-grid {
    grid-template-columns: 1fr;
  }

  section.related-tools-section .related-tools-grid > div {
    padding: 47px 20px 20px;
  }
}

@media screen and (max-width: 576px) {
  /* Hero buttons: flex -> grid */
  section.related-tools-section .flex.gap-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  section.related-tools-section .flex.gap-2 button {
    width: 100%;
    justify-content: center;
  }

  section.related-tools-section .flex.gap-2 button:first-child {
    margin-right: 0;
  }
}

@media screen and (max-width: 380px) {
  section.related-tools-section .flex.gap-2 {
    grid-template-columns: 1fr;
  }
}
</style>
